import React, { useState } from "react";
import { Form, message } from "antd";
import { appQueryClient as queryClient } from "../../../shared/state/appQueryClient";
import { COREModal } from "../../../COREDesignSystem/Overlay/COREModal";
import { generateTestId } from "../../../shared/testids/testids";
import { COREForm } from "../../../COREDesignSystem/Form/COREForm";
import { COREInput } from "../../../COREDesignSystem/Form/COREInput";
import { CORERadiobuttonGroup } from "../../../COREDesignSystem/Form/CORERadioButton";
import { COREFormItem } from "../../../COREDesignSystem/Form/COREFormItem";
import { CORETypographyInput } from "../../../COREDesignSystem/Typography/CORETypographyInput";
import { COREButton } from "../../../COREDesignSystem/Action/COREButton";
import { COREBody } from "../../../COREDesignSystem/Typography/COREBody";

export type FeedBack = {
  summary: string;
  detail: string;
  initialFeedBackOption: "feedback" | "problem" | "suggestion" | "question";
};

export const ModalFeedback: React.FC<{
  isFeedbackModalOpen: boolean;
  setIsFeedbackModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  initialValue?: FeedBack;
}> = ({ isFeedbackModalOpen, setIsFeedbackModalOpen, initialValue }) => {
  const [feedbackOption, setFeedbackOption] = useState(
    initialValue?.initialFeedBackOption || "feedback"
  );
  const [form] = Form.useForm();

  const onCancelForm = (): void => {
    form.resetFields();
    setIsFeedbackModalOpen(false);
  };

  const onSubmitForm = (): void => {
    form.validateFields().then((values) => {
      queryClient
        .fetchQuery({
          queryKey: [
            "feedbacks",
            {
              action: "feedbacks",
              fetchOptions: {
                body: JSON.stringify({
                  type: feedbackOption,
                  summary: values.summary, // eslint-disable-next-line camelcase
                  tell_us_more: values.detail ?? null,
                }),
              },
              body: {
                body: JSON.stringify({
                  type: feedbackOption,
                  summary: values.summary, // eslint-disable-next-line camelcase
                  tell_us_more: values.detail ?? null,
                }),
              }.body,
              enabled: true,
            },
          ],
        })
        .then(() => {
          message.success("Your feedback has been sent");
          form.resetFields();
          setIsFeedbackModalOpen(false);
        })
        .catch((error: Error) => {
          message.error(error.toString());
        });
    });
  };

  return (
    <COREModal
      title="Feedback"
      open={isFeedbackModalOpen}
      onCancel={onCancelForm}
      testID={generateTestId("user", "feedback")}
      type={"basic"}
      width={"600px"}
      footer={[
        <COREButton
          key="submit"
          type={"primary"}
          size={"lg"}
          onClick={onSubmitForm}
        >
          Submit
        </COREButton>,
        <COREButton
          key="cancel"
          type={"default"}
          size={"lg"}
          onClick={onCancelForm}
        >
          Cancel
        </COREButton>,
      ]}
    >
      <COREForm
        form={form}
        initialValues={initialValue ?? {}}
        testID={generateTestId("home", "feedback-form")}
        layout={"horizontal"}
      >
        <COREBody>
          We would love to hear your thoughts, suggestions, problems, or general
          feedback so we can make our product even better.
        </COREBody>
        <COREFormItem
          testID={generateTestId("home", "feedback-type-item")}
          label={
            <CORETypographyInput type={"label"}>
              Feedback type
            </CORETypographyInput>
          }
        >
          <CORERadiobuttonGroup
            testID={generateTestId("home", "feedback-type")}
            radioBtnValues={[
              {
                name: "Feedback",
                value: "feedback",
                testID: generateTestId("home", "feedback-type-feedback"),
              },
              {
                name: "Problem",
                value: "problem",
                testID: generateTestId("home", "feedback-type-problem"),
              },
              {
                name: "Suggestion",
                value: "suggestion",
                testID: generateTestId("home", "feedback-type-suggestion"),
              },
              {
                name: "Question",
                value: "question",
                testID: generateTestId("home", "feedback-type-question"),
              },
            ]}
            value={feedbackOption}
            onChange={(e) => setFeedbackOption(e.target.value)}
          />
        </COREFormItem>
        <COREFormItem
          testID={generateTestId("home", "feedback-summary-item")}
          label={
            <CORETypographyInput type={"label"}>Summary</CORETypographyInput>
          }
          name="summary"
          rules={[{ required: true, message: "Please input summary!" }]}
        >
          <COREInput.Input
            testID={generateTestId("home", "feedback-summary")}
          />
        </COREFormItem>

        <COREFormItem
          testID={generateTestId("home", "feedback-detail-item")}
          label={
            <CORETypographyInput type={"label"}>
              Description
            </CORETypographyInput>
          }
          name="detail"
        >
          <COREInput.TextArea
            style={{ height: "150px" }}
            allowResize
            testID={generateTestId("home", "feedback-detail")}
          />
        </COREFormItem>
      </COREForm>
    </COREModal>
  );
};
